import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { storeWriteSettingsAdapts, StoreWriteSettingsAdapter } from './store-write-settings.adapter';
import { CopySink } from 'app/model/pipeline/copysink/copysink';
import { SftpStoreWriteSettings } from 'app/model/pipeline/copysink/store-write-settings/sftp-store-write-settings';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import moment from 'moment-timezone';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SftpStoreWriteSettings),
    asynchronous: true,
    adapts: {
        ...storeWriteSettingsAdapts,
        operationTimeout: '*',
        useTempFileRename: '*'
    }
})
export class SftpStoreWriteSettingsAdapter extends StoreWriteSettingsAdapter<
    SftpStoreWriteSettings,
    Model.StoreWriteSettings
> {
    protected override async _toResource(model: SftpStoreWriteSettings): Promise<Model.StoreWriteSettings> {
        const resource: Model.StoreWriteSettings = await super._toResource(model);
        StoreWriteSettingsAdapter.toStoreWriteSettingsResource(model, resource);
        if (model != null && resource != null) {
            try {
                resource.operationTimeout =
                    typeof model.operationTimeout === 'number'
                        ? moment.duration(model.operationTimeout, 'minutes').format('hh:mm:ss', {
                              trim: false
                          })
                        : ValueAdapter.toResource(model, SftpStoreWriteSettings.OPERATION_TIMEOUT);
            } catch {
                this._logError('Failed to convert operationTimeout into TimeSpan!');
            }
            resource.useTempFileRename = ValueAdapter.toResource(model, SftpStoreWriteSettings.USE_TEMPFILE_RENAME);
        }
        return resource;
    }

    protected override async _toModel(
        resource: Model.StoreWriteSettings,
        copySink: CopySink
    ): Promise<SftpStoreWriteSettings> {
        const model = await super._toModel(resource, copySink);
        StoreWriteSettingsAdapter.toStoreWriteSettingsModel(resource, model);
        if (model != null && resource != null) {
            try {
                if (resource.operationTimeout == null) {
                    model.operationTimeout = undefined;
                } else {
                    if (
                        typeof resource.operationTimeout === 'string' &&
                        !isLogicAppExpression(resource.operationTimeout)
                    ) {
                        model.operationTimeout = moment.duration(resource.operationTimeout).asMinutes();
                    } else {
                        ValueAdapter.toModel(
                            model,
                            SftpStoreWriteSettings.OPERATION_TIMEOUT,
                            resource.operationTimeout
                        );
                    }
                }
            } catch {
                this._logError('Failed to convert operationTimeout into number!');
            }
            ValueAdapter.toModel(model, SftpStoreWriteSettings.USE_TEMPFILE_RENAME, resource.useTempFileRename);
        }
        return model;
    }
}
